import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { publicApi } from '../../lib/api';
import { getSessionId, readUserParams } from '../../lib/userParams';
import { Loader2, Check, ArrowRight } from 'lucide-react';

/**
 * Pre-click confirmation popup for an offer. Rendered when offer.qualifier.enabled === true.
 * On confirm, POSTs a qualifier submission (so it appears in Customer Journey), then
 * invokes onConfirm(answer) so the parent can open the cloaked track URL.
 */
export default function OfferQualifierDialog({ open, offer, onOpenChange, onConfirm }) {
  const q = offer?.qualifier || {};
  const type = q.type || 'slider';
  const min = Number.isFinite(q.min) ? q.min : 5;
  const max = Number.isFinite(q.max) ? q.max : 50;
  const step = Number.isFinite(q.step) && q.step > 0 ? q.step : 5;
  const unit = q.unit || '';
  const options = Array.isArray(q.options) ? q.options : [];
  const [value, setValue] = useState(q.default ?? min);
  const [text, setText] = useState('');
  const [choice, setChoice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValue(q.default ?? min);
    setText('');
    setChoice(options[0]?.value || '');
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offer?.id]);

  if (!offer) return null;

  const readAnswer = () => {
    if (type === 'slider') return { value, label: `${unit}${value}` };
    if (type === 'choice') {
      const opt = options.find((o) => String(o.value) === String(choice));
      return { value: choice, label: opt?.label || String(choice) };
    }
    return { value: text.trim(), label: text.trim() };
  };

  const disabled = (() => {
    if (submitting) return true;
    if (type === 'choice') return !choice;
    if (type === 'input') return !text.trim();
    return false;
  })();

  const handleConfirm = async () => {
    const ans = readAnswer();
    setSubmitting(true);
    try {
      await publicApi.submitForm('qualifier', {
        sessionId: getSessionId(),
        offer_id: offer.id,
        offer_title: offer.title,
        cta_url: offer.cta_url || '',
        question: q.title || '',
        field_name: q.field_name || 'answer',
        answer: ans.value,
        answer_label: ans.label,
        type,
        ...readUserParams(),
      });
    } catch {
      // Do not block redirect even if the submission failed; log-only.
    }
    onConfirm?.(ans);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="offer-qualifier-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl leading-tight">{q.title || `Confirm before continuing to ${offer.title}`}</DialogTitle>
        </DialogHeader>
        {q.subtitle && <p className="text-sm text-slate-600 -mt-1">{q.subtitle}</p>}

        <div className="py-3 space-y-4">
          {type === 'slider' && (
            <div data-testid="qualifier-slider">
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Amount</span>
                <span className="font-serif-display text-3xl font-bold text-emerald-700">{unit}{value}</span>
              </div>
              <Slider
                value={[value]}
                min={min}
                max={max}
                step={step}
                onValueChange={(v) => setValue(v[0])}
              />
              <div className="mt-2 flex justify-between text-[11px] text-slate-500 font-medium">
                <span>{unit}{min}</span>
                <span>{unit}{max}</span>
              </div>
            </div>
          )}

          {type === 'choice' && (
            <div className="grid gap-2" data-testid="qualifier-choices">
              {options.map((o) => (
                <button
                  key={String(o.value)}
                  type="button"
                  onClick={() => setChoice(o.value)}
                  className={`w-full text-left rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                    String(choice) === String(o.value)
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${String(choice) === String(o.value) ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300'}`}>
                      {String(choice) === String(o.value) && <Check className="w-2.5 h-2.5 text-white" />}
                    </span>
                    {o.label}
                  </span>
                </button>
              ))}
            </div>
          )}

          {type === 'input' && (
            <div data-testid="qualifier-input">
              <Label className="text-xs">Your answer</Label>
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                type={q.input_type === 'number' ? 'number' : 'text'}
                placeholder={q.placeholder || ''}
                className="mt-1 h-11"
              />
            </div>
          )}
        </div>

        <div className="pt-1">
          <Button
            onClick={handleConfirm}
            disabled={disabled}
            data-testid="qualifier-confirm-btn"
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <>
                {q.confirm_label || 'Confirm & Continue'} <ArrowRight className="w-4 h-4 ml-1.5" />
              </>
            )}
          </Button>
          <p className="mt-2 text-[11px] text-slate-500 text-center">Your response is saved for personalisation. You'll be taken to the partner's site next.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
